import React, { useEffect, useState, useRef } from 'react';
import axios from '../axiosInstance';
import { useLocation } from 'react-router-dom';
import {
    FiSearch,
    FiAlertCircle,
    FiBox,
    FiChevronLeft,
    FiChevronRight,
    FiX,
    FiLoader,
    FiCheckCircle,
} from 'react-icons/fi';

export default function Withdrawal() {
    const location = useLocation();
    // 'create' for Stock Withdrawal, 'list' for Get All
    // We check if the path contains 'create' or 'get-all' to set the internal logic
    const activeTab = location.pathname.includes('create') ? 'create' : 'list';

    // =========================================
    // Shared / Dropdown Data
    // =========================================
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    const [states, setStates] = useState([]);
    const [purposes, setPurposes] = useState([]);
    const [warehouses, setWarehouses] = useState([]);
    const [filteredWarehouses, setFilteredWarehouses] = useState([]); // For State -> Warehouse dependency
    const [commodities, setCommodities] = useState([]);
    const [schemes, setSchemes] = useState([]);

    // =========================================
    // Selection State (Mimicking BlockStock)
    // =========================================
    const [selected, setSelected] = useState({
        purpose_id: '',
        state_id: '',
        warehouse_id: '',
    });

    // Extra Filters (visible after warehouse selection, similar to BlockStock)
    const [selectedCommodity, setSelectedCommodity] = useState('');
    const [selectedScheme, setSelectedScheme] = useState('');

    // Search terms for custom dropdowns
    const [purposeSearch, setPurposeSearch] = useState('');
    const [stateSearch, setStateSearch] = useState('');
    const [warehouseSearch, setWarehouseSearch] = useState('');
    const [commoditySearch, setCommoditySearch] = useState('');
    const [schemeSearch, setSchemeSearch] = useState('');

    // Dropdown open states
    const [purposeDropdownOpen, setPurposeDropdownOpen] = useState(false);
    const [stateDropdownOpen, setStateDropdownOpen] = useState(false);
    const [warehouseDropdownOpen, setWarehouseDropdownOpen] = useState(false);
    const [commodityDropdownOpen, setCommodityDropdownOpen] = useState(false);
    const [schemeDropdownOpen, setSchemeDropdownOpen] = useState(false);

    // =========================================
    // Table Data State
    // =========================================
    const [data, setData] = useState([]);
    const [tableLoading, setTableLoading] = useState(false);
    const [page, setPage] = useState(1);
    const [limit, setLimit] = useState(10);
    const [totalPages, setTotalPages] = useState(1);
    const [selectedRows, setSelectedRows] = useState(new Set()); // Set of item IDs

    // =========================================
    // Withdrawal Popup State
    // =========================================
    const [isPopupOpen, setIsPopupOpen] = useState(false);
    const [selectedItem, setSelectedItem] = useState(null);
    const [withdrawQty, setWithdrawQty] = useState('');
    const [withdrawBags, setWithdrawBags] = useState('');
    const [remarks, setRemarks] = useState('');
    const [remarksOptions, setRemarksOptions] = useState([]);
    const [supportingDocument, setSupportingDocument] = useState(null);
    const [popupError, setPopupError] = useState('');
    const [popupSuccess, setPopupSuccess] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);

    const token = localStorage.getItem('token');
    const listsLoadedRef = useRef(false);

    // =========================================
    // 1. Fetch Dropdowns on Mount
    // =========================================
    useEffect(() => {
        if (listsLoadedRef.current) return;
        listsLoadedRef.current = true;
        const fetchLists = async () => {
            setLoading(true);
            try {
                const [
                    statesRes,
                    purposesRes,
                    warehousesRes,
                    commoditiesRes,
                    schemesRes,
                    remarksRes,
                ] = await Promise.all([
                    axios.get(`${process.env.REACT_APP_BASE_URL}states`),
                    axios.get(`${process.env.REACT_APP_BASE_URL}purposes?paginate=false`),
                    axios.get(`${process.env.REACT_APP_BASE_URL}warehouse?paginate=false`),
                    axios.get(`${process.env.REACT_APP_BASE_URL}commodity`),
                    axios.get(`${process.env.REACT_APP_BASE_URL}schemes`),
                    axios.get(`${process.env.REACT_APP_BASE_URL}remarks`),
                ]);
                setStates(statesRes.data || []);
                setPurposes(purposesRes.data || []);
                setWarehouses(warehousesRes.data || []);
                setCommodities(commoditiesRes.data || []);
                setSchemes(schemesRes.data || []);
                setRemarksOptions(remarksRes.data || []);
            } catch (err) {
                setError('Failed to load dropdowns. Please refresh.');
            } finally {
                setLoading(false);
            }
        };
        fetchLists();
    }, []);

    // =========================================
    // 2. Filter Warehouses by State
    // =========================================
    useEffect(() => {
        if (selected.state_id) {
            setFilteredWarehouses(
                warehouses.filter((w) => w.state?.id === selected.state_id)
            );
        } else {
            setFilteredWarehouses([]);
        }
    }, [selected.state_id, warehouses]);

    // Cleanup selections when switching tabs/routes
    useEffect(() => {
        clearSelections();
        // If switching to list, we might want to fetch initially
        if (location.pathname.includes('get-all')) {
            // Trigger fetch? 
            // The useEffect below depends on activeTab, which depends on location. 
            // And fetchData depends on activeTab.
            // We just need to make sure we don't clear data AFTER fetch if execution order is tricky.
            // But this useEffect runs on location change. The fetch one runs on activeTab change.
            // Since activeTab is derived from location during render, it changes instantly.
            // This effect clears filters. Then fetch runs.
        }
    }, [location.pathname]);


    // =========================================
    // 3. Fetch Table Data (Auto-fetch logic)
    // =========================================
    const fetchData = async () => {

        if (activeTab === 'create' && !selected.warehouse_id) {
            setData([]);
            setTotalPages(1);
            return;
        }

        setTableLoading(true);
        setError('');
        try {
            const params = new URLSearchParams();
            params.append('page', page);
            params.append('limit', limit);

            // Append filters
            if (selected.warehouse_id) params.append('warehouse_id', selected.warehouse_id);
            if (selected.state_id) params.append('state_id', selected.state_id);
            if (selected.purpose_id) params.append('purpose_id', selected.purpose_id);
            if (selectedCommodity) params.append('commodity_id', selectedCommodity);
            if (selectedScheme) params.append('scheme_id', selectedScheme);

            let endpoint = '';
            if (activeTab === 'create') {
                endpoint = `${process.env.REACT_APP_BASE_URL}stock-blocking?${params.toString()}`;
            } else {
                endpoint = `${process.env.REACT_APP_BASE_URL}stock-withdrawal?${params.toString()}`;
            }

            const response = await axios.get(endpoint, {
                headers: { Authorization: `Bearer ${token}` },
            });
            setData(response.data.data || []);
            setTotalPages(response.data.totalPages || 1);
        } catch (err) {
            console.error(err);
            let msg = 'Failed to fetch data';
            if (err.response?.data?.message) msg = err.response.data.message;
            setError(msg);
            setData([]);
        } finally {
            setTableLoading(false);
        }
    };

    // Trigger fetch when dependencies change
    useEffect(() => {
        fetchData();
        // eslint-disable-next-line
    }, [
        page,
        limit,
        activeTab, // This now changes when URL changes
        selected.warehouse_id,
        selectedCommodity,
        selectedScheme,
    ]);

    // Handle clearing text filters when switching
    const clearSelections = () => {
        setSelected({ purpose_id: '', state_id: '', warehouse_id: '' });
        setSelectedCommodity('');
        setSelectedScheme('');
        setPurposeSearch('');
        setStateSearch('');
        setWarehouseSearch('');
        setCommoditySearch('');
        setSchemeSearch('');
        setData([]);
        setSelectedRows(new Set());
    };

    // Selection handlers
    const handleSelectRow = (id) => {
        setSelectedRows((prev) => {
            const newSet = new Set(prev);
            if (newSet.has(id)) {
                newSet.delete(id);
            } else {
                newSet.add(id);
            }
            return newSet;
        });
    };

    const handleSelectAll = (e) => {
        if (e.target.checked) {
            const allIds = data.map((row) => row.id);
            setSelectedRows(new Set(allIds));
        } else {
            setSelectedRows(new Set());
        }
    };

    const calculateTotalSelectedQty = () => {
        return data
            .filter((row) => selectedRows.has(row.id))
            .reduce((sum, row) => sum + parseFloat(row.blocked_qty || 0), 0)
            .toFixed(4);
    };

    const calculateTotalSelectedBags = () => {
        return data
            .filter((row) => selectedRows.has(row.id))
            .reduce((sum, row) => sum + parseFloat(row.bags || 0), 0)
            .toFixed(2);
    };

    // =========================================
    // Handlers
    // =========================================

    const openWithdrawalPopup = (item) => {
        // If item is provided, it's a single item (though we are removing buttons, this is still useful)
        // If item is null, we use selectedRows
        if (item) {
            setSelectedItem(item);
            setWithdrawQty(item.blocked_qty);
            setWithdrawBags(item.blocked_bags || item.bags || '');
        } else {
            const selectedItems = data.filter(row => selectedRows.has(row.id));
            // If only one item selected, treat as single withdrawal (allow editing)
            if (selectedItems.length === 1) {
                const singleItem = selectedItems[0];
                setSelectedItem(singleItem); // Set as single object, not array
                setWithdrawQty(singleItem.blocked_qty);
                setWithdrawBags(singleItem.blocked_bags || singleItem.bags || '');
            } else {
                setSelectedItem(selectedItems); // Array for bulk
                setWithdrawQty(calculateTotalSelectedQty());
                setWithdrawBags(calculateTotalSelectedBags());
            }
        }

        setRemarks('');
        setSupportingDocument(null);
        setPopupError('');
        setPopupSuccess('');
        setIsPopupOpen(true);
    };

    const closeWithdrawalPopup = () => {
        setIsPopupOpen(false);
        setSelectedItem(null);
    };

    const handleSaveWithdrawal = async () => {
        if (!withdrawQty || parseFloat(withdrawQty) <= 0) {
            setPopupError('Please enter a valid quantity.');
            return;
        }
        if (!remarks) {
            setPopupError('Please select a remark.');
            return;
        }
        setIsSubmitting(true);
        setPopupError('');
        try {
            const formData = new FormData();
            const isBulk = Array.isArray(selectedItem);

            if (isBulk) {
                // Bulk operation
                selectedItem.forEach(item => {
                    formData.append('block_ids[]', item.id);
                });
            } else {
                // Single operation
                formData.append('block_id', selectedItem.id);
                formData.append('withdrawn_qty', parseFloat(withdrawQty));
                formData.append('bags', parseFloat(withdrawBags || 0));
            }

            formData.append('remarks', remarks);
            if (supportingDocument) {
                formData.append('file', supportingDocument);
            }

            const endpoint = isBulk
                ? `${process.env.REACT_APP_BASE_URL}stock-withdrawal/bulk`
                : `${process.env.REACT_APP_BASE_URL}stock-withdrawal`;

            const response = await axios.post(
                endpoint,
                formData,
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        'Content-Type': 'multipart/form-data'
                    }
                }
            );
            setPopupSuccess(response.data.message || "Stock withdrawn successfully");
            setTimeout(() => {
                closeWithdrawalPopup();
                if (activeTab === 'create') {
                    fetchData();
                    setSelectedRows(new Set());
                }
            }, 1000);
        } catch (err) {
            console.error(err);
            let msg = 'Failed to save withdrawal.';
            if (err.response?.data?.message) msg = err.response.data.message;
            setPopupError(msg);
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className="min-h-screen bg-gradient-to-br from-white via-blue-50 to-blue-100 p-10  text-gray-900">
            <div className="flex items-center gap-3 mb-6">
                <FiBox className="text-3xl text-blue-700" />
                <h2 className="text-2xl md:text-3xl font-extrabold text-blue-800 tracking-tight">
                    {activeTab === 'create' ? 'Stock Withdrawal' : 'Withdrawal List'}
                </h2>
            </div>

            {/* No Tabs anymore - switched by Sidebar/URL */}

            {/* Dropdowns (Visible mostly for Create tab, but can be used for List filtering too) */}
            <div className="flex flex-wrap gap-4 mb-6 items-center">
                {/* Purpose Dropdown */}
                <div className="relative w-[250px] max-w-full">
                    <div
                        onClick={() => !loading && setPurposeDropdownOpen((prev) => !prev)}
                        className={`px-4 py-2 border rounded-xl bg-white shadow-sm cursor-pointer text-base flex justify-between items-center h-[50px] ${loading ? 'opacity-60 cursor-not-allowed' : ''
                            }`}
                    >
                        <span className="truncate flex-1 text-left">
                            {selected.purpose_id
                                ? purposeSearch || purposes.find(p => p.id === selected.purpose_id)?.name || 'Purpose Selected'
                                : 'Select Purpose'}
                        </span>
                        <div className="flex items-center gap-2 ml-2">
                            {selected.purpose_id && (
                                <FiX
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        setSelected({ ...selected, purpose_id: '', state_id: '', warehouse_id: '' });
                                        setPurposeSearch('');
                                        setStateSearch('');
                                        setWarehouseSearch('');
                                    }}
                                    className="text-gray-400 hover:text-red-500 text-lg cursor-pointer"
                                    title="Clear"
                                />
                            )}
                            <span className="text-gray-500">{purposeDropdownOpen ? '▲' : '▼'}</span>
                        </div>
                    </div>
                    {purposeDropdownOpen && (
                        <div className="absolute top-full left-0 right-0 bg-white border rounded-xl shadow-lg max-h-72 overflow-y-auto mt-1 z-10">
                            <input
                                type="text"
                                placeholder="Search Purpose..."
                                value={purposeSearch}
                                onClick={(e) => e.stopPropagation()}
                                onChange={(e) => setPurposeSearch(e.target.value)}
                                className="w-full px-3 py-2 border-b text-sm focus:outline-none"
                            />
                            {purposes
                                .filter((p) => (p?.name || '').toLowerCase().includes(purposeSearch.toLowerCase()))
                                .map((p) => (
                                    <div
                                        key={p.id}
                                        onClick={() => {
                                            setSelected({ ...selected, purpose_id: p.id, state_id: '', warehouse_id: '' });
                                            setPurposeSearch(p.name);
                                            setPurposeDropdownOpen(false);
                                            setStateSearch('');
                                            setWarehouseSearch('');
                                            setPage(1);
                                        }}
                                        className={`p-2 cursor-pointer hover:bg-blue-100 ${selected.purpose_id === p.id ? 'bg-blue-200 font-semibold' : ''}`}
                                    >
                                        {p.name}
                                    </div>
                                ))}
                        </div>
                    )}
                </div>

                {/* State Dropdown */}
                <div className="relative w-[250px] max-w-full">
                    <div
                        onClick={() => selected.purpose_id && !loading && setStateDropdownOpen((prev) => !prev)}
                        className={`px-4 py-2 border rounded-xl bg-white shadow-sm text-base flex justify-between items-center h-[50px] ${!selected.purpose_id || loading ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer'
                            }`}
                    >
                        <span className="truncate flex-1 text-left">
                            {selected.state_id
                                ? stateSearch || states.find(s => s.id === selected.state_id)?.name || 'State Selected'
                                : 'Select State'}
                        </span>
                        <div className="flex items-center gap-2 ml-2">
                            {selected.state_id && (
                                <FiX
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        setSelected({ ...selected, state_id: '', warehouse_id: '' });
                                        setStateSearch('');
                                        setWarehouseSearch('');
                                    }}
                                    className="text-gray-400 hover:text-red-500 text-lg cursor-pointer"
                                    title="Clear"
                                />
                            )}
                            <span className="text-gray-500">{stateDropdownOpen ? '▲' : '▼'}</span>
                        </div>
                    </div>
                    {stateDropdownOpen && (
                        <div className="absolute top-full left-0 right-0 bg-white border rounded-xl shadow-lg max-h-72 overflow-y-auto mt-1 z-10">
                            <input
                                type="text"
                                placeholder="Search State..."
                                value={stateSearch}
                                onClick={(e) => e.stopPropagation()}
                                onChange={(e) => setStateSearch(e.target.value)}
                                className="w-full px-3 py-2 border-b text-sm focus:outline-none"
                            />
                            {states
                                .filter((s) => (s?.name || '').toLowerCase().includes(stateSearch.toLowerCase()))
                                .map((s) => (
                                    <div
                                        key={s.id}
                                        onClick={() => {
                                            setSelected({ ...selected, state_id: s.id, warehouse_id: '' });
                                            setStateSearch(s.name);
                                            setStateDropdownOpen(false);
                                            setWarehouseSearch('');
                                            setPage(1);
                                        }}
                                        className={`p-2 cursor-pointer hover:bg-blue-100 ${selected.state_id === s.id ? 'bg-blue-200 font-semibold' : ''}`}
                                    >
                                        {s.name}
                                    </div>
                                ))}
                        </div>
                    )}
                </div>

                {/* Warehouse Dropdown */}
                <div className="relative w-[250px] max-w-full">
                    <div
                        onClick={() => selected.state_id && !loading && setWarehouseDropdownOpen((prev) => !prev)}
                        className={`px-4 py-2 border rounded-xl bg-white shadow-sm text-base flex justify-between items-center h-[50px] ${!selected.state_id || loading ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer'
                            }`}
                    >
                        <span className="truncate flex-1 text-left">
                            {selected.warehouse_id
                                ? warehouseSearch || warehouses.find(w => w.id === selected.warehouse_id)?.warehouse_name || 'Warehouse Selected'
                                : 'Select Warehouse'}
                        </span>
                        <div className="flex items-center gap-2 ml-2">
                            {selected.warehouse_id && (
                                <FiX
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        setSelected({ ...selected, warehouse_id: '' });
                                        setWarehouseSearch('');
                                    }}
                                    className="text-gray-400 hover:text-red-500 text-lg cursor-pointer"
                                    title="Clear"
                                />
                            )}
                            <span className="text-gray-500">{warehouseDropdownOpen ? '▲' : '▼'}</span>
                        </div>
                    </div>
                    {warehouseDropdownOpen && (
                        <div className="absolute top-full left-0 right-0 bg-white border rounded-xl shadow-lg max-h-72 overflow-y-auto mt-1 z-10">
                            <input
                                type="text"
                                placeholder="Search Warehouse..."
                                value={warehouseSearch}
                                onClick={(e) => e.stopPropagation()}
                                onChange={(e) => setWarehouseSearch(e.target.value)}
                                className="w-full px-3 py-2 border-b text-sm focus:outline-none"
                            />
                            {filteredWarehouses
                                .filter((w) => (w?.warehouse_name || '').toLowerCase().includes(warehouseSearch.toLowerCase()))
                                .map((w) => (
                                    <div
                                        key={w.id}
                                        onClick={() => {
                                            setSelected({ ...selected, warehouse_id: w.id });
                                            setWarehouseSearch(w.warehouse_name);
                                            setWarehouseDropdownOpen(false);
                                            setPage(1);
                                        }}
                                        className={`p-2 cursor-pointer hover:bg-blue-100 ${selected.warehouse_id === w.id ? 'bg-blue-200 font-semibold' : ''}`}
                                    >
                                        {w.warehouse_name}
                                    </div>
                                ))}
                        </div>
                    )}
                </div>
            </div>

            {/* Extra Filters (Commodity / Scheme) - Visible when Warehouse is Selected */}
            {selected.warehouse_id && (
                <div className="flex flex-wrap gap-4 mb-6 items-center">
                    {/* Commodity Dropdown */}
                    <div className="relative w-[250px] max-w-full">
                        <div
                            onClick={() => setCommodityDropdownOpen((prev) => !prev)}
                            className="px-4 py-2 border rounded-xl bg-white shadow-sm cursor-pointer text-base flex justify-between items-center h-[50px]"
                        >
                            <span className="truncate flex-1 text-left">
                                {selectedCommodity
                                    ? commoditySearch || commodities.find(c => c.id === selectedCommodity)?.commodityname || 'Commodity Selected'
                                    : 'Select Commodity'}
                            </span>
                            <div className="flex items-center gap-2 ml-2">
                                {selectedCommodity && (
                                    <FiX
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            setSelectedCommodity('');
                                            setCommoditySearch('');
                                        }}
                                        className="text-gray-400 hover:text-red-500 text-lg cursor-pointer"
                                        title="Clear"
                                    />
                                )}
                                <span className="text-gray-500">{commodityDropdownOpen ? '▲' : '▼'}</span>
                            </div>
                        </div>
                        {commodityDropdownOpen && (
                            <div className="absolute top-full left-0 right-0 bg-white border rounded-xl shadow-lg max-h-72 overflow-y-auto mt-1 z-10">
                                <input
                                    type="text"
                                    placeholder="Search Commodity..."
                                    value={commoditySearch}
                                    onClick={(e) => e.stopPropagation()}
                                    onChange={(e) => setCommoditySearch(e.target.value)}
                                    className="w-full px-3 py-2 border-b text-sm focus:outline-none"
                                />
                                {commodities
                                    .filter((c) => (c?.commodityname || '').toLowerCase().includes(commoditySearch.toLowerCase()))
                                    .map((c) => (
                                        <div
                                            key={c.id}
                                            onClick={() => {
                                                setSelectedCommodity(c.id);
                                                setCommoditySearch(c.commodityname);
                                                setCommodityDropdownOpen(false);
                                                setPage(1);
                                            }}
                                            className={`p-2 cursor-pointer hover:bg-blue-100 ${selectedCommodity === c.id ? 'bg-blue-200 font-semibold' : ''}`}
                                        >
                                            {c.commodityname}
                                        </div>
                                    ))}
                            </div>
                        )}
                    </div>

                    {/* Scheme Dropdown */}
                    <div className="relative w-[250px] max-w-full">
                        <div
                            onClick={() => setSchemeDropdownOpen((prev) => !prev)}
                            className="px-4 py-2 border rounded-xl bg-white shadow-sm cursor-pointer text-base flex justify-between items-center h-[50px]"
                        >
                            <span className="truncate flex-1 text-left">
                                {selectedScheme
                                    ? schemeSearch || schemes.find(s => s.id === selectedScheme)?.name || 'Scheme Selected'
                                    : 'Select Scheme'}
                            </span>
                            <div className="flex items-center gap-2 ml-2">
                                {selectedScheme && (
                                    <FiX
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            setSelectedScheme('');
                                            setSchemeSearch('');
                                        }}
                                        className="text-gray-400 hover:text-red-500 text-lg cursor-pointer"
                                        title="Clear"
                                    />
                                )}
                                <span className="text-gray-500">{schemeDropdownOpen ? '▲' : '▼'}</span>
                            </div>
                        </div>
                        {schemeDropdownOpen && (
                            <div className="absolute top-full left-0 right-0 bg-white border rounded-xl shadow-lg max-h-72 overflow-y-auto mt-1 z-10">
                                <input
                                    type="text"
                                    placeholder="Search Scheme..."
                                    value={schemeSearch}
                                    onClick={(e) => e.stopPropagation()}
                                    onChange={(e) => setSchemeSearch(e.target.value)}
                                    className="w-full px-3 py-2 border-b text-sm focus:outline-none"
                                />
                                {schemes
                                    .filter((s) => (s?.name || '').toLowerCase().includes(schemeSearch.toLowerCase()))
                                    .map((s) => (
                                        <div
                                            key={s.id}
                                            onClick={() => {
                                                setSelectedScheme(s.id);
                                                setSchemeSearch(s.name);
                                                setSchemeDropdownOpen(false);
                                                setPage(1);
                                            }}
                                            className={`p-2 cursor-pointer hover:bg-blue-100 ${selectedScheme === s.id ? 'bg-blue-200 font-semibold' : ''}`}
                                        >
                                            {s.name}
                                        </div>
                                    ))}
                            </div>
                        )}
                    </div>
                </div>
            )}


            {/* Error */}
            {error && (
                <div className="mb-6 flex items-center gap-2 p-4 bg-red-100 border border-red-300 text-red-700 rounded-lg text-base font-semibold">
                    <FiAlertCircle className="text-xl" />
                    {error}
                </div>
            )}

            {/* Table */}
            <div className="overflow-hidden rounded-2xl shadow-xl border border-gray-100 bg-white">
                <div className="overflow-x-auto">
                    <table className="w-full text-left border-collapse min-w-[1100px]">
                        <thead className="bg-gray-100 border-b border-gray-200">
                            <tr>
                                {activeTab === 'create' ? (
                                    <>
                                        <th className="px-6 py-4 text-left text-sm font-semibold text-gray-600 uppercase tracking-wider">
                                            <input
                                                type="checkbox"
                                                className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                                                onChange={handleSelectAll}
                                                checked={data.length > 0 && selectedRows.size === data.length}
                                            />
                                        </th>
                                        <th className="px-6 py-4 text-left text-sm font-semibold text-gray-600 uppercase tracking-wider">State</th>
                                        <th className="px-6 py-4 text-left text-sm font-semibold text-gray-600 uppercase tracking-wider">Warehouse</th>
                                        <th className="px-6 py-4 text-left text-sm font-semibold text-gray-600 uppercase tracking-wider">WHR Id</th>
                                        <th className="px-6 py-4 text-left text-sm font-semibold text-gray-600 uppercase tracking-wider">Commodity</th>
                                        <th className="px-6 py-4 text-left text-sm font-semibold text-gray-600 uppercase tracking-wider">Scheme</th>
                                        <th className="px-6 py-4 text-left text-sm font-semibold text-gray-600 uppercase tracking-wider">Qty(MT)</th>
                                        <th className="px-6 py-4 text-left text-sm font-semibold text-gray-600 uppercase tracking-wider">Bags</th>
                                        <th className="px-6 py-4 text-left text-sm font-semibold text-gray-600 uppercase tracking-wider">Purpose</th>
                                    </>
                                ) : (
                                    <>
                                        <th className="px-6 py-4 text-left text-sm font-semibold text-gray-600 uppercase tracking-wider">State</th>
                                        <th className="px-6 py-4 text-left text-sm font-semibold text-gray-600 uppercase tracking-wider">Warehouse</th>
                                        <th className="px-6 py-4 text-left text-sm font-semibold text-gray-600 uppercase tracking-wider">Warehouse Code</th>
                                        <th className="px-6 py-4 text-left text-sm font-semibold text-gray-600 uppercase tracking-wider">Purpose</th>
                                        <th className="px-6 py-4 text-left text-sm font-semibold text-gray-600 uppercase tracking-wider">Quantity</th>
                                        <th className="px-6 py-4 text-left text-sm font-semibold text-gray-600 uppercase tracking-wider">Withdrawn Bags</th>
                                        <th className="px-6 py-4 text-left text-sm font-semibold text-gray-600 uppercase tracking-wider">Status</th>
                                        <th className="px-6 py-4 text-left text-sm font-semibold text-gray-600 uppercase tracking-wider">Created At</th>
                                    </>
                                )}
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-100">
                            {tableLoading ? (
                                <tr>
                                    <td
                                        colSpan={8}
                                        className="text-center py-10 text-blue-600 text-lg font-semibold"
                                    >
                                        <div className="flex justify-center items-center gap-2">
                                            <FiLoader className="animate-spin text-2xl" />
                                            <span>Loading data...</span>
                                        </div>
                                    </td>
                                </tr>
                            ) : data.length === 0 ? (
                                <tr>
                                    <td
                                        colSpan={9}
                                        className="text-center py-12 text-gray-500 font-medium text-lg"
                                    >
                                        <div className="flex flex-col items-center gap-2">
                                            <FiAlertCircle className="text-3xl text-gray-400" />
                                            <span>
                                                {activeTab === 'create' && !selected.warehouse_id
                                                    ? "Please select Purpose, State, and Warehouse to view stock."
                                                    : "No data found."}
                                            </span>
                                        </div>
                                    </td>
                                </tr>
                            ) : (
                                data.map((item) => (
                                    <tr
                                        key={item.id}
                                        className="hover:bg-gray-50 transition-colors duration-200"
                                    >
                                        {activeTab === 'create' ? (
                                            <>
                                                <td className="px-6 py-4 whitespace-nowrap">
                                                    <input
                                                        type="checkbox"
                                                        className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                                                        checked={selectedRows.has(item.id)}
                                                        onChange={() => handleSelectRow(item.id)}
                                                    />
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700 font-medium">{item.state?.name}</td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-blue-900">
                                                    {item.warehouse?.warehouse_name}
                                                    <span className="text-sm text-gray-500">
                                                        <br />
                                                        {item.warehouse?.warehouse_code}
                                                    </span>

                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">{item.whr_id}</td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">{item.commodity?.commodityname}</td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm">
                                                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800">
                                                        {item.scheme?.name}
                                                    </span>
                                                </td>
                                                <td className="px-6 py-4 text-sm">
                                                    <div className="flex flex-col gap-1">
                                                        <div className="whitespace-nowrap"><span className="text-gray-500 text-xs">Total:</span> <span className="inline-block px-2 py-0.5 rounded-lg bg-green-50 text-green-700 font-bold border border-green-100">{item.actual_qty}</span></div>
                                                        <div className="whitespace-nowrap"><span className="text-gray-500 text-xs">Blocked:</span> <span className="inline-block px-2 py-0.5 rounded-lg bg-red-50 text-red-700 font-bold border border-red-100">{item.blocked_qty}</span></div>
                                                        <div className="whitespace-nowrap"><span className="text-gray-500 text-xs">Current:</span> <span className="inline-block px-2 py-0.5 rounded-lg bg-blue-50 text-blue-700 font-bold border border-blue-100">{item.remaining_qty}</span></div>
                                                        <div className="whitespace-nowrap"><span className="text-gray-500 text-xs">Withdrawn:</span> <span className="inline-block px-2 py-0.5 rounded-lg bg-gray-50 text-gray-700 font-bold border border-gray-100">{item.withdrawal_qty}</span></div>
                                                    </div>
                                                </td>
                                                <td className="px-6 py-4 text-sm">
                                                    <div className="flex flex-col gap-1">
                                                        <div className="whitespace-nowrap"><span className="text-gray-500 text-xs">Total:</span> <span className="text-gray-700 font-medium">{item.actual_bags}</span></div>
                                                        <div className="whitespace-nowrap"><span className="text-gray-500 text-xs">Blocked:</span> <span className="inline-block px-2 py-0.5 rounded-lg bg-red-50 text-red-700 font-bold border border-red-100">{item.blocked_bags}</span></div>
                                                        <div className="whitespace-nowrap"><span className="text-gray-500 text-xs">Current:</span> <span className="inline-block px-2 py-0.5 rounded-lg bg-blue-50 text-blue-700 font-bold border border-blue-100">{item.current_bags}</span></div>
                                                        <div className="whitespace-nowrap"><span className="text-gray-500 text-xs">Withdrawn:</span> <span className="inline-block px-2 py-0.5 rounded-lg bg-gray-50 text-gray-700 font-bold border border-gray-100">{item.withdrawal_bags}</span></div>
                                                    </div>
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-purple-700 font-medium">{item.purpose?.name}</td>
                                            </>
                                        ) : (
                                            <>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700 font-medium">{item.state_name}</td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm font-bold text-gray-900">{item.warehouse_name}</td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">{item.warehouse_code}</td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-purple-700 font-medium">{item.purpose_name}</td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm font-bold text-gray-900">{item.quantity}</td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm font-bold text-gray-900">{item.withdrawn_bags}</td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                                                    <span className={`px-2 py-1 rounded-full text-xs font-semibold ${item.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                                                        }`}>
                                                        {item.status?.toUpperCase()}
                                                    </span>
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                                                    {item.createdat ? (
                                                        <span className="text-xs font-medium text-gray-500 bg-gray-100 px-3 py-1 rounded-full border border-gray-200 shadow-sm">
                                                            {new Date(item.createdat).toLocaleString()}
                                                        </span>
                                                    ) : '-'}
                                                </td>
                                            </>
                                        )}
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Selection Summary Bar */}
            {selectedRows.size > 0 && (
                <div className="fixed bottom-6 left-1/2 transform -translate-x-1/2 z-50 w-full max-w-4xl px-4 animate-in fade-in slide-in-from-bottom-4 duration-300">
                    <div className="bg-blue-900 border border-blue-800 shadow-2xl rounded-2xl p-4 flex items-center justify-between">
                        <div className="flex items-center gap-6">
                            <div className="flex flex-col">
                                <span className="text-blue-200 text-xs font-semibold uppercase tracking-wider">Selected</span>
                                <span className="text-white text-lg font-bold">{selectedRows.size} Stocks</span>
                            </div>
                            <div className="h-10 w-px bg-blue-700/50"></div>
                            <div className="flex flex-col">
                                <span className="text-blue-200 text-xs font-semibold uppercase tracking-wider">Total Quantity</span>
                                <span className="text-white text-lg font-bold">{calculateTotalSelectedQty()} MT</span>
                            </div>
                            <div className="h-10 w-px bg-blue-700/50"></div>
                            <div className="flex flex-col">
                                <span className="text-blue-200 text-xs font-semibold uppercase tracking-wider">Total Bags</span>
                                <span className="text-white text-lg font-bold">{calculateTotalSelectedBags()} Nos</span>
                            </div>
                        </div>
                        <div className="flex items-center gap-3">
                            <button
                                onClick={() => setSelectedRows(new Set())}
                                className="px-4 py-2 text-blue-200 hover:text-white font-medium transition-colors"
                            >
                                Clear
                            </button>
                            <button
                                onClick={() => openWithdrawalPopup(null)}
                                className="px-6 py-3 bg-white hover:bg-blue-50 text-blue-900 font-bold rounded-xl transition shadow-lg hover:shadow-xl active:scale-95"
                            >
                                Proceed to Withdraw
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Pagination mode */}
            {totalPages > 1 && (
                <div className="flex flex-col md:flex-row justify-between items-center gap-4 mt-8 text-base">
                    <div className="flex items-center gap-2">
                        <button
                            disabled={page === 1 || tableLoading}
                            onClick={() => setPage(page - 1)}
                            className="px-4 py-2 bg-blue-200 hover:bg-blue-300 disabled:opacity-40 rounded-lg font-medium flex items-center gap-1"
                        >
                            <FiChevronLeft /> Prev
                        </button>
                        <span className="text-gray-700 font-semibold">
                            Page {page} of {totalPages}
                        </span>
                        <button
                            disabled={page === totalPages || tableLoading}
                            onClick={() => setPage(page + 1)}
                            className="px-4 py-2 bg-blue-200 hover:bg-blue-300 disabled:opacity-40 rounded-lg font-medium flex items-center gap-1"
                        >
                            Next <FiChevronRight />
                        </button>
                    </div>
                </div>
            )}


            {/* Withdrawal Popup */}
            {isPopupOpen && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
                    <div className="bg-white rounded-xl shadow-2xl w-full max-w-md p-6 relative">
                        <button
                            onClick={closeWithdrawalPopup}
                            className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
                        >
                            <FiX size={24} />
                        </button>

                        <h3 className="text-xl font-bold text-gray-800 mb-4">Stock Withdrawal</h3>

                        {popupSuccess ? (
                            <div className="flex flex-col items-center justify-center py-6 text-green-600">
                                <FiCheckCircle size={48} className="mb-2" />
                                <p className="font-semibold">{popupSuccess}</p>
                            </div>
                        ) : (
                            <>
                                <div className="mb-4">
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Quantity <span className="text-red-500">*</span>
                                    </label>
                                    <input
                                        type="number"
                                        value={withdrawQty}
                                        readOnly={Array.isArray(selectedItem)}
                                        onChange={(e) => setWithdrawQty(e.target.value)}
                                        className={`w-full px-3 py-2 border border-gray-300 rounded-lg outline-none transition ${Array.isArray(selectedItem) ? 'bg-gray-100 cursor-not-allowed' : 'focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500'}`}
                                        placeholder="Enter Quantity"
                                    />
                                </div>
                                <div className="mb-4">
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Bag Quantity
                                    </label>
                                    <input
                                        type="number"
                                        value={withdrawBags}
                                        readOnly={Array.isArray(selectedItem)}
                                        onChange={(e) => setWithdrawBags(e.target.value)}
                                        className={`w-full px-3 py-2 border border-gray-300 rounded-lg outline-none transition ${Array.isArray(selectedItem) ? 'bg-gray-100 cursor-not-allowed' : 'focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500'}`}
                                        placeholder="Enter Bag Quantity"
                                    />
                                </div>

                                <div className="mb-4">
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Remarks <span className="text-red-500">*</span>
                                    </label>
                                    <select
                                        value={remarks}
                                        onChange={(e) => setRemarks(e.target.value)}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none transition"
                                    >
                                        <option value="">Select Remarks</option>
                                        {remarksOptions.map((remark, index) => (
                                            <option key={index} value={remark}>{remark}</option>
                                        ))}
                                    </select>
                                </div>

                                <div className="mb-6">
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Supporting Document <span className="text-gray-400 text-xs">(Optional)</span>
                                    </label>
                                    <input
                                        type="file"
                                        onChange={(e) => setSupportingDocument(e.target.files[0])}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none transition"
                                    />
                                    {supportingDocument && (
                                        <p className="text-sm text-gray-500 mt-1">Selected: {supportingDocument.name}</p>
                                    )}
                                </div>

                                {popupError && (
                                    <div className="mb-4 p-2 bg-red-50 border border-red-200 text-red-600 rounded text-sm">
                                        {popupError}
                                    </div>
                                )}

                                <div className="flex gap-3 justify-end">
                                    <button
                                        onClick={closeWithdrawalPopup}
                                        className="px-4 py-2 text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-lg font-medium transition"
                                        disabled={isSubmitting}
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        onClick={handleSaveWithdrawal}
                                        disabled={isSubmitting}
                                        className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg font-medium shadow-sm transition flex items-center gap-2"
                                    >
                                        {isSubmitting && <FiLoader className="animate-spin" />}
                                        Save
                                    </button>
                                </div>
                            </>
                        )}
                    </div>
                </div>
            )}

        </div>
    );
}
